import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { buildTrafficYoY, pctChange } from '../utils/webTraffic';
import { formatNumberAbbreviated } from './utils';

// A seasonal overlay (a "cycle plot"): the x-axis is the calendar cycle Jan→Dec
// and each year is its own line, so the same month sits on the same tick across
// years and the year-over-year comparison is read VERTICALLY between lines.
//
// This form also solves a problem grouped bars could not. A bar occupies a
// reserved slot within its month's band, so a month with only one year on file
// leaves a lone bar sitting well off its tick — close enough to the next
// month's bar that the two read as one month's pair. A line's point sits
// exactly on its month, so single-year months are unambiguous and all the
// collected data can stay on the chart.
//
// NEUTRAL greys stepped by RECENCY: the newest year is black, each earlier year
// lighter. Indexed by how many years back the year sits, NOT by its position in
// this stock's list, so the newest line is the same shade on a stock with two
// years of data as on one with three.
//
// This replaced a blue ramp, which failed for a structural reason worth keeping
// in mind: a hue has to stay inside a usable lightness band to remain
// recognisably that hue, which caps how far apart consecutive steps can sit. In
// practice #256abf and #0d366b read as the same dark blue. Neutrals have the
// whole range from black to near-white to spend, so the same number of steps
// separates far more cleanly.
//
// It also makes colour mean one thing in this section. The up/down deltas in
// the headline and the matched-month table are green/red; with neutral lines,
// green and red are the ONLY chromatic colour here and they always mean
// direction.
//
// The lightest step is #a8a8a8 (2.38:1 on white), not the 25% grey that would
// complete an even black/75/50/25 progression — pure 25% grey is 1.84:1 and a
// 2px stroke at that weight fades toward the card. Steps are chosen for STROKE,
// which carries far less ink than a fill. Only the first three are reachable
// while the fetch window spans 25 months; the fourth is validated and waiting.
//
// Validated on white as an ordinal ramp: lightness strictly monotone, every
// adjacent ΔL >= 0.06, light end clears 2:1. The validator's "single hue" check
// reports a 90° spread and fails — that is it measuring hue on achromatic
// colours, where hue is undefined. A neutral ramp is single-hue by definition;
// the check exists to catch rainbow ramps and does not apply.
const YEAR_RAMP_BY_RECENCY = ['#000000', '#404040', '#808080', '#a8a8a8'];

// A 25-month fetch window can surface at most three calendar years. Raise this
// only alongside the window, and re-validate: each added step has to keep every
// adjacent ΔL above the floor AND keep the lightest step readable as a stroke,
// which the range runs out of well before it runs out of arithmetic.
const MAX_YEARS = 3;

const AXIS_INK = '#6b7280';
const GRID_INK = '#e5e7eb';
const UP_INK = '#16a34a';
const DOWN_INK = '#dc2626';
const FLAT_INK = '#6b7280';
const BODY_INK = '#374151';

// The small uppercase caption that introduces the headline and the table.
// Callers supply their own marginBottom; everything else is shared so the two
// captions cannot drift into looking like different kinds of label.
const SECTION_LABEL = {
  fontSize: '11px',
  color: AXIS_INK,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

// Matched-month table metrics. Every numeric column carries identical padding
// so the header rules line up with the rows beneath them.
const MONTH_CELL_PAD = '3px 8px 3px 0';
const VALUE_CELL_PAD = '3px 0 3px 8px';

// Recharts takes an explicit domain literally and prints its raw endpoints as
// axis labels — "86.14M, 106.14M, 126.14M". Snapping the DOMAIN to round
// numbers instead is no better: it forces the bounds outward, which on a stock
// with one seasonal spike drags the floor all the way back to zero and then
// hands Recharts a range it subdivides unevenly (0, 15M, 30M, 50M).
//
// So the domain stays exact and the TICKS are generated here: round multiples
// that happen to fall inside it. The axis simply has no label at its very
// bottom, which costs nothing and keeps the non-zero baseline.
const niceStep = (rough) => {
  if (!(rough > 0)) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;
  const snapped = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return snapped * magnitude;
};

const ticksWithin = (lo, hi, step) => {
  const out = [];
  for (let t = Math.ceil(lo / step) * step; t <= hi; t += step) out.push(t);
  return out;
};

const axisTicks = (lo, hi) => {
  const step = niceStep((hi - lo) / 5);
  const ticks = ticksWithin(lo, hi, step);
  // A 1/2/5/10 ladder can land a step that only fits three labels in the range.
  // Halving it is always still a round number and reads far better.
  return ticks.length >= 4 ? ticks : ticksWithin(lo, hi, step / 2);
};

const deltaInk = (changePct) => {
  if (changePct > 0) return UP_INK;
  if (changePct < 0) return DOWN_INK;
  return FLAT_INK;
};

// The sign and the arrow carry the direction on their own, so the colour is
// reinforcement rather than the only channel.
const formatDelta = (changePct) => {
  const arrow = changePct > 0 ? '▲' : changePct < 0 ? '▼' : '■';
  return `${arrow} ${changePct > 0 ? '+' : ''}${changePct.toFixed(1)}%`;
};

const hostnameOf = (website) => {
  if (!website) return null;
  try {
    return new URL(website.startsWith('http') ? website : `https://${website}`).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
};

const ChartTooltip = ({ active, payload, label, yearColors }) => {
  if (!active || !payload?.length) return null;

  // Recharts hands over one entry per rendered Line. Sorting descending by year
  // puts the most recent on top, matching how the headline reads.
  const rows = [...payload]
    .filter((entry) => entry.value != null)
    .sort((a, b) => Number(b.dataKey) - Number(a.dataKey));
  if (!rows.length) return null;

  // The year-over-year delta for THIS month, computed from the two points the
  // reader is looking at — never from a different month's pair.
  const newest = rows[0];
  const prior = rows.find((entry) => Number(entry.dataKey) === Number(newest.dataKey) - 1);
  const changePct = prior ? pctChange(newest.value, prior.value) : null;

  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        padding: '8px 10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        fontSize: '12px',
      }}
    >
      <div style={{ fontWeight: 600, color: '#111827', marginBottom: '4px' }}>{label}</div>
      {rows.map((entry) => (
        <div key={entry.dataKey} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: BODY_INK }}>
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '2px',
              background: yearColors[entry.dataKey],
              flexShrink: 0,
            }}
          />
          <span style={{ minWidth: '32px' }}>{entry.dataKey}</span>
          <span style={{ fontWeight: 600 }}>{formatNumberAbbreviated(entry.value)}</span>
        </div>
      ))}
      {changePct != null && (
        <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #f3f4f6', color: deltaInk(changePct), fontWeight: 600 }}>
          {formatDelta(changePct)} year over year
        </div>
      )}
    </div>
  );
};

/**
 * Same-month year-over-year web traffic for one stock.
 *
 * Renders nothing at all unless there is at least one matched pair — a frame
 * around "no comparison available" is worse than no frame, and a lone year's
 * line would invite exactly the month-over-month reading this section exists to
 * avoid.
 */
const WebTrafficSection = ({ stock }) => {
  const yoy = useMemo(() => buildTrafficYoY(stock?.trafficData), [stock?.trafficData]);

  // Every collected month is plotted, including those with a single year on
  // file — a point sits exactly on its own tick, so it cannot be mistaken for
  // the neighbouring month the way an off-centre lone bar could.
  const { chartYears, yearColors, yDomain, yTicks, hasInteriorGap } = useMemo(() => {
    if (!yoy) return { chartYears: [], yearColors: {}, yDomain: [0, 'auto'], yTicks: undefined, hasInteriorGap: false };

    const present = yoy.years.slice(-MAX_YEARS);
    const latestYear = present[present.length - 1];
    const keys = present.map(String);

    // A zero baseline is mandatory for bars, whose LENGTH carries the value,
    // but not for lines, which encode position. Established sites vary by a few
    // percent year to year, so anchoring at zero pins every line into a flat
    // band across the top third of the plot and the years overlap illegibly.
    //
    // The padding is proportional to the data's own spread rather than a fixed
    // percentage of the maximum, so a stock whose years genuinely sit close
    // together does not get its 2% difference stretched to look like a
    // collapse. The exact figures are stated in the headline and the table; the
    // chart's job is the shape.
    const values = yoy.months.flatMap((month) => keys.map((key) => month[key]).filter((v) => v != null));
    const min = Math.min(...values);
    const max = Math.max(...values);
    const spread = max - min;
    const pad = spread > 0 ? spread * 0.35 : max * 0.1;
    const lo = Math.max(0, min - pad);
    const hi = max + pad * 0.5;

    // Whether any line has an INTERIOR gap — a month with no value sitting
    // between two months that have one, for the same year. That is the only
    // case that draws a visible break, and so the only case the caption should
    // explain.
    //
    // "This year is missing a month some other year has" is a different and
    // much broader condition: a stock whose 2025 line covers Oct-Dec and whose
    // 2026 line covers May-Jul satisfies it while neither line is actually
    // broken. Captioning on that would explain a break that is not on screen.
    const hasInteriorGap = keys.some((key) => {
      const filled = yoy.months.map((month) => month[key] != null);
      const first = filled.indexOf(true);
      const last = filled.lastIndexOf(true);
      return first !== -1 && filled.slice(first, last + 1).includes(false);
    });

    return {
      chartYears: keys,
      yearColors: Object.fromEntries(
        present.map((year) => [
          String(year),
          YEAR_RAMP_BY_RECENCY[latestYear - year] || YEAR_RAMP_BY_RECENCY[YEAR_RAMP_BY_RECENCY.length - 1],
        ]),
      ),
      yDomain: [lo, hi],
      yTicks: axisTicks(lo, hi),
      hasInteriorGap,
    };
  }, [yoy]);

  // Only a total absence of usable traffic hides the section. A stock WITH data
  // but no matched pair still renders: 46% of symbols were first collected in
  // 2025-10 or later and have no prior year at all, and hiding the section for
  // them is indistinguishable from "we hold no traffic data for this company".
  // Showing the months we do have, and saying plainly that no comparison exists
  // yet, beats looking broken.
  if (!yoy) return null;

  const { latestPair, latestMonth, earliestMonth, months, pairs } = yoy;
  const domain = hostnameOf(stock?.website);

  // The newest collected month is routinely newer than the newest comparable
  // one, because 2025-03 → 2025-09 was never collected and the provider's
  // three-month retention put it permanently out of reach. Saying so is the
  // difference between a stale number and a mislabelled one.
  const trailsLatest = latestPair && latestMonth
    && (latestMonth.year !== latestPair.year || latestMonth.monthIndex !== latestPair.monthIndex);

  // With no pair, the earliest month on file is what dates the first possible
  // comparison — that same month, a year on. Phrased as a condition rather than
  // a promise, because it only happens if collection is still running then.
  const firstComparableLabel = !latestPair && earliestMonth
    ? `${earliestMonth.label.slice(0, 3)} ${earliestMonth.year + 1}`
    : null;

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-4">
      <h3 className="text-xl font-semibold mb-1">
        {/* The ramp's middle step, so the heading reads as part of the same
            family as the lines without impersonating any one year. */}
        <FontAwesomeIcon icon={faGlobe} className="mr-2" style={{ color: YEAR_RAMP_BY_RECENCY[1] }} />
        Web Traffic
      </h3>
      {/* The subtitle must not promise a comparison the panel is not showing. */}
      <p style={{ fontSize: '13px', color: AXIS_INK, margin: '0 0 16px' }}>
        {latestPair
          ? `Monthly visits${domain ? ` to ${domain}` : ''}, compared against the same calendar month a year earlier — traffic is seasonal, so month-to-month movement mostly measures the calendar.`
          : `Monthly visits${domain ? ` to ${domain}` : ''}. Traffic is seasonal, so the meaningful comparison is against the same calendar month a year earlier — which needs a second year on file.`}
      </p>

      {/* With a pair, the headline is the comparison. Without one, it is the
          newest month's level, labelled with the month it belongs to — never a
          percentage or an arrow, which would imply a comparison happened. */}
      {latestPair ? (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ ...SECTION_LABEL, marginBottom: '2px' }}>
            {latestPair.periodLabel} vs {latestPair.priorPeriodLabel}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '26px', fontWeight: 700, color: deltaInk(latestPair.changePct), lineHeight: 1.1 }}>
              {formatDelta(latestPair.changePct)}
            </span>
            <span style={{ fontSize: '13px', color: AXIS_INK }}>
              {formatNumberAbbreviated(latestPair.previous)} → {formatNumberAbbreviated(latestPair.current)} visits
            </span>
          </div>
        </div>
      ) : latestMonth && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ ...SECTION_LABEL, marginBottom: '2px' }}>{latestMonth.label}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '26px', fontWeight: 700, color: BODY_INK, lineHeight: 1.1 }}>
              {formatNumberAbbreviated(latestMonth.visits)}
            </span>
            <span style={{ fontSize: '13px', color: AXIS_INK }}>visits</span>
          </div>
        </div>
      )}

      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={months} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={GRID_INK} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: AXIS_INK }}
            tickLine={false}
            axisLine={{ stroke: GRID_INK }}
            padding={{ left: 12, right: 12 }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: AXIS_INK }}
            tickLine={false}
            axisLine={false}
            width={52}
            domain={yDomain}
            ticks={yTicks}
            tickFormatter={(value) => formatNumberAbbreviated(value)}
          />
          {/* The crosshair is what makes the vertical read explicit: it lines
              every year's value for that month up in one tooltip. */}
          <Tooltip
            cursor={{ stroke: '#9ca3af', strokeWidth: 1, strokeDasharray: '3 3' }}
            content={<ChartTooltip yearColors={yearColors} />}
          />
          {/* Recharts lays legend items out inline with no separation, so the
              year labels run together as "202420252026". The gap has to come
              from the item style, not the wrapper. */}
          <Legend
            wrapperStyle={{ fontSize: '12px', paddingTop: '4px' }}
            formatter={(value) => <span style={{ color: AXIS_INK, marginRight: '12px' }}>{value}</span>}
            iconType="plainline"
            iconSize={14}
          />
          {chartYears.map((year) => (
            <Line
              key={year}
              type="linear"
              dataKey={year}
              name={year}
              stroke={yearColors[year]}
              strokeWidth={2}
              // Dots are load-bearing, not decoration. A year whose only
              // collected month has no collected neighbour has no segment to
              // draw, so without a dot that month renders as nothing at all.
              dot={{ r: 4, fill: yearColors[year], strokeWidth: 0 }}
              activeDot={{ r: 5, stroke: '#ffffff', strokeWidth: 2 }}
              // Gaps in coverage must stay gaps. Connecting across them would
              // draw a straight line through months nobody measured and invite
              // reading it as a flat trend.
              connectNulls={false}
              // The panel re-renders whenever React Query refetches the list in
              // the background; Recharts replays its draw-in on every one of
              // those, so the lines visibly flicker while the reader is looking
              // at them.
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      {(trailsLatest || firstComparableLabel || hasInteriorGap) && (
        <p style={{ fontSize: '12px', color: AXIS_INK, marginTop: '8px', marginBottom: 0 }}>
          {trailsLatest && `Collected through ${latestMonth.label}, but no month after ${latestPair.periodLabel} has a prior-year counterpart on file, so that is the most recent comparison available. `}
          {firstComparableLabel && `No year-over-year comparison yet: collection for this site starts at ${earliestMonth.label}, so no month has a prior-year counterpart on file. The first comparison becomes possible once ${firstComparableLabel} is collected. `}
          {hasInteriorGap && 'A break in a line means those months were never collected — a gap in coverage, not months without visitors.'}
        </p>
      )}

      {pairs.length > 1 && (
        <div style={{ marginTop: '14px' }}>
          <div style={{ ...SECTION_LABEL, marginBottom: '6px' }}>
            Every matched month
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: '12px', width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: AXIS_INK, textAlign: 'right' }}>
                  <th scope="col" style={{ textAlign: 'left', fontWeight: 500, padding: MONTH_CELL_PAD }}>Month</th>
                  <th scope="col" style={{ fontWeight: 500, padding: VALUE_CELL_PAD }}>Prior year</th>
                  <th scope="col" style={{ fontWeight: 500, padding: VALUE_CELL_PAD }}>Visits</th>
                  <th scope="col" style={{ fontWeight: 500, padding: VALUE_CELL_PAD }}>Change</th>
                </tr>
              </thead>
              <tbody>
                {[...pairs].reverse().map((pair) => (
                  <tr key={`${pair.year}-${pair.monthIndex}`} style={{ borderTop: '1px solid #f3f4f6', textAlign: 'right' }}>
                    <th scope="row" style={{ textAlign: 'left', fontWeight: 500, color: BODY_INK, padding: MONTH_CELL_PAD }}>
                      {pair.periodLabel}
                    </th>
                    <td style={{ color: AXIS_INK, padding: VALUE_CELL_PAD }}>{formatNumberAbbreviated(pair.previous)}</td>
                    <td style={{ color: BODY_INK, padding: VALUE_CELL_PAD }}>{formatNumberAbbreviated(pair.current)}</td>
                    <td style={{ color: deltaInk(pair.changePct), fontWeight: 600, padding: VALUE_CELL_PAD }}>
                      {formatDelta(pair.changePct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebTrafficSection;
